import type { AdminPlanRetryResult } from '@steado/admin-contract/plan-runs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { planTone } from '@/lib/plan-tone';
import { Field, Fields, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDateTime, formatDuration, humanise } from '@/lib/format';

import { planRunQueryOptions, retryPlanRun } from './plan-ops-query';
import { planRunTone } from './plan-run-tone';

interface PlanRunDrawerProps {
  readonly runId: string;
  readonly onClose: () => void;
}

export function PlanRunDrawer({ runId, onClose }: PlanRunDrawerProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(planRunQueryOptions(api, runId));

  const [isConfirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AdminPlanRetryResult | null>(null);

  const retry = useMutation({
    mutationFn: () => retryPlanRun(api, runId),
    onSuccess: (retried) => {
      setResult(retried);
      setConfirming(false);
      // The week is now generating, and a new run will appear behind the drawer.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'plan-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'plan-run', runId] });
    },
    // Never retried automatically: each attempt enqueues a generation and writes
    // an audit row.
    retry: false,
  });

  const run = query.data;
  const blocked = run !== undefined && !run.retryable;

  return (
    <>
      <Drawer
        title={run === undefined ? 'Plan run' : `Week of ${run.weekStartDate}`}
        {...(run === undefined ? {} : { subtitle: run.id })}
        onClose={onClose}
        footer={
          run === undefined ? undefined : (
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(true);
              }}
              disabled={!can.mutate || blocked || retry.isPending}
              // No tooltip for `blocked`: the reason is already on the page as
              // the note below, and saying it twice is saying it once badly.
              title={can.mutate ? undefined : 'Re-running generation requires the admin role.'}
            >
              {retry.isPending ? 'Queueing…' : 'Retry generation…'}
            </Button>
          )
        }
      >
        {query.isPending ? <TabSkeleton rows={4} /> : null}
        {query.isError ? (
          <TabError message={query.error.message} onRetry={() => void query.refetch()} />
        ) : null}

        {run === undefined ? null : (
          <div className="flex flex-col gap-5">
            <Panel
              title="Run"
              action={<BadgeCell label={humanise(run.status)} tone={planRunTone(run.status)} dot />}
            >
              <Fields>
                <Field label="Subscriber" value={run.displayName ?? run.userId} />
                <Field label="Week starting" value={run.weekStartDate} />
                <Field label="Trigger" value={humanise(run.trigger)} />
                {/* 2 means the repair round ran — not that the week was retried. */}
                <Field
                  label="Model passes"
                  value={run.attempt === 1 ? '1 (no repair round)' : `${String(run.attempt)} (repaired)`}
                />
                <Field label="Duration" value={formatDuration(run.durationMs)} />
                <Field label="Started" value={formatDateTime(run.startedAt)} />
                <Field
                  label="Finished"
                  value={run.finishedAt === null ? null : formatDateTime(run.finishedAt)}
                />
                <Field
                  label="Week now"
                  value={
                    run.planWeekStatus === null ? (
                      <span className="text-muted-foreground">week deleted</span>
                    ) : (
                      <BadgeCell label={run.planWeekStatus} tone={planTone(run.planWeekStatus)} />
                    )
                  }
                />
              </Fields>
            </Panel>

            {run.validationFailure === null ? null : (
              <Panel title="What the validator rejected">
                {/*
                  Verbatim, numbers and all: "Thursday protein 88g, floor 110g"
                  is the whole of what makes a failed run actionable.
                */}
                <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground">
                  {run.validationFailure}
                </pre>
              </Panel>
            )}

            {run.boundsRelaxation === null ? null : (
              <Panel title="What the targets could not deliver">
                {/*
                  Above the validator's own message on purpose: when this is
                  set, it is usually the reason for whatever that says. A day
                  that was never reachable failing its numbers is not the same
                  story as a model that got the arithmetic wrong.
                */}
                <p className="text-sm leading-relaxed text-muted-foreground">{run.boundsRelaxation}</p>
              </Panel>
            )}

            {run.failureReason === null ? null : (
              <Panel title="Failure">
                <p className="text-sm leading-relaxed text-muted-foreground">{run.failureReason}</p>
              </Panel>
            )}

            {result === null ? null : (
              <Panel title="Retry result" action={<BadgeCell label="queued" tone="info" dot />}>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  The week of {result.weekStartDate} is generating again.
                </p>
              </Panel>
            )}

            {retry.isError ? (
              <Alert role="alert" tone="err">
                <AlertDescription>{retry.error.message}</AlertDescription>
              </Alert>
            ) : null}

            {blocked ? (
              <Alert role="note" tone="warn">
                <AlertDescription>{run.retryBlockedReason}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Drawer>

      {isConfirming && run !== undefined ? (
        <ConfirmDialog
          title={`Regenerate the week of ${run.weekStartDate}?`}
          body={
            <p>
              The week is queued for generation again. Whatever is planned for it now is replaced when it
              succeeds.
            </p>
          }
          confirmLabel="Regenerate"
          isPending={retry.isPending}
          {...(retry.isError ? { error: retry.error.message } : {})}
          onConfirm={() => {
            retry.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
}
